import { API_BASE_URL } from '../../lib/apiBaseUrl';

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
};

const waitForCurrentUser = (firebase, timeoutMs = 10000) =>
  new Promise((resolve, reject) => {
    const auth = firebase?.auth;
    if (!auth?.onAuthStateChanged) {
      reject(new Error('Firebase auth is not available.'));
      return;
    }

    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const timer = setTimeout(() => {
      unsubscribe?.();
      reject(new Error('No signed-in Firebase user found.'));
    }, timeoutMs);

    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        if (!user) return;
        clearTimeout(timer);
        unsubscribe();
        resolve(user);
      },
      (error) => {
        clearTimeout(timer);
        unsubscribe();
        reject(error);
      },
    );
  });

const getIdToken = async (firebase, forceRefresh = false) => {
  const currentUser = firebase?.auth?.currentUser || (await waitForCurrentUser(firebase));
  return currentUser.getIdToken(forceRefresh);
};

const stripHtmlTags = (value = '') =>
  String(value)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeApiErrorText = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const hasHtml = /^<!doctype/i.test(raw) || /<\/?[a-z][^>]*>/i.test(raw);
  const text = hasHtml ? stripHtmlTags(raw) : raw;
  const routeMatch = text.match(/Cannot\s+(GET|POST|PUT|PATCH|DELETE|OPTIONS)\s+([^\s]+)/i);
  if (routeMatch) {
    const path = routeMatch[2].startsWith('/') ? routeMatch[2] : `/${routeMatch[2]}`;
    return `Drive API endpoint is unavailable (${path}). Please verify server deployment and REACT_APP_API_BASE_URL.`;
  }

  return text;
};

const parseError = async (response) => {
  const contentType = String(response.headers?.get?.('content-type') || '').toLowerCase();
  try {
    if (contentType.includes('application/json')) {
      const data = await response.json();
      const normalizedMessage = normalizeApiErrorText(data?.error || data?.message || '');
      if (!normalizedMessage) return data;
      return {
        ...data,
        error: normalizedMessage,
      };
    }
  } catch (_err) {
    // Fall through to text parsing.
  }
  try {
    const text = (await response.text()).trim();
    if (text) {
      return { error: normalizeApiErrorText(text) || text };
    }
  } catch (_err) {
    // Ignore read failures.
  }
  return { error: response.statusText };
};

const apiFetch = async (firebase, path, options = {}, params) => {
  const token = await getIdToken(firebase);

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {}),
  };

  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      ...options,
      headers,
    });
  } catch (err) {
    const error = new Error(
      `Cannot reach Drive backend at ${API_BASE_URL}. Make sure the server is running and REACT_APP_API_BASE_URL is correct.`,
    );
    error.code = 'DRIVE_BACKEND_UNREACHABLE';
    error.status = 0;
    error.reconnect = false;
    error.cause = err;
    throw error;
  }

  if (!response.ok) {
    const errorPayload = await parseError(response);
    const normalizedMessage = normalizeApiErrorText(errorPayload.error || errorPayload.message || '');
    let message = normalizedMessage || 'Drive API request failed.';
    if (!normalizedMessage && response.status === 404) {
      message = `Drive API endpoint not found at ${API_BASE_URL}.`;
    } else if (!normalizedMessage && response.status === 401) {
      message = 'Drive authentication failed. Please sign in again.';
    }
    const error = new Error(message);
    error.code = errorPayload.code || 'DRIVE_API_ERROR';
    error.status = response.status;
    error.reconnect = Boolean(errorPayload.reconnect);
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

const withRoot = (params, rootType) => {
  if (!rootType) return params;
  return { ...(params || {}), root: rootType };
};

export const driveDownloadUrl = async (firebase, fileId, options = {}) => {
  const token = await getIdToken(firebase, Boolean(options.forceRefresh));
  const rootType = options.rootType || '';
  const params = new URLSearchParams();
  params.set('fileId', fileId);
  if (rootType) params.set('root', rootType);
  params.set('token', token);
  return `${API_BASE_URL}/api/drive/download?${params.toString()}`;
};

export const driveDownloadBlobUrl = async (firebase, fileId, options = {}) => {
  const rootType = options.rootType || '';
  const params = new URLSearchParams();
  params.set('fileId', fileId);
  if (rootType) params.set('root', rootType);
  const url = `${API_BASE_URL}/api/drive/download?${params.toString()}`;

  const fetchWithToken = async (forceRefresh = false) => {
    const token = await getIdToken(firebase, forceRefresh);
    return fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  let response = await fetchWithToken(Boolean(options.forceRefresh));
  if (response.status === 401 && !options.forceRefresh) {
    response = await fetchWithToken(true);
  }
  if (!response.ok) {
    const error = new Error('Drive download failed.');
    error.status = response.status;
    throw error;
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const startGoogleDriveAuth = async (
  firebase,
  params = /** @type {{ loginHint?: string; hd?: string }} */ ({}),
) => {
  const returnTo = typeof window !== 'undefined' ? window.location.origin : '';
  const data = await apiFetch(
    firebase,
    '/api/auth/google/start',
    { method: 'GET' },
    { ...params, ...(returnTo ? { returnTo } : {}) },
  );
  return data.url;
};

export const driveRoot = async (firebase, options = {}) => {
  try {
    const data = await apiFetch(
      firebase,
      '/api/drive/root',
      { method: 'GET' },
      withRoot(null, options.rootType),
    );
    return data.rootFolderId;
  } catch (error) {
    if (error.status === 404 || error.code === 'DRIVE_NOT_CONNECTED') {
      return null;
    }
    throw error;
  }
};

export const driveStatus = async (firebase, options = {}) => {
  try {
    const data = await apiFetch(
      firebase,
      '/api/drive/status',
      { method: 'GET' },
      withRoot(null, options.rootType),
    );
    return data;
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    const looksLikeMissingStatusRoute =
      Number(error?.status) === 404 &&
      (message.includes('/api/drive/status') || error?.code === 'API_ROUTE_NOT_FOUND');
    if (!looksLikeMissingStatusRoute) {
      throw error;
    }

    const rootFolderId = await driveRoot(firebase, options);
    return {
      connected: Boolean(rootFolderId),
      rootFolderId: rootFolderId || null,
      rootType: options.rootType || 'assets',
      fallback: true,
    };
  }
};

export const driveDisconnect = async (firebase, options = {}) =>
  apiFetch(
    firebase,
    '/api/drive/disconnect',
    { method: 'POST' },
    withRoot(null, options.rootType),
  );

export const driveList = async (firebase, folderId, options = {}) =>
  apiFetch(
    firebase,
    '/api/drive/list',
    { method: 'GET' },
    withRoot({ folderId }, options.rootType),
  );

export const driveFolders = async (firebase, parentId, options = {}) =>
  apiFetch(
    firebase,
    '/api/drive/folders',
    { method: 'GET' },
    withRoot({ parentId }, options.rootType),
  );

export const drivePath = async (firebase, folderId, options = {}) =>
  apiFetch(
    firebase,
    '/api/drive/path',
    { method: 'GET' },
    withRoot({ folderId }, options.rootType),
  );

export const driveView = async (firebase, folderId, options = {}, params = {}) =>
  (async () => {
    try {
      return await apiFetch(
        firebase,
        '/api/drive/view',
        { method: 'GET', signal: options.signal },
        withRoot({ folderId, ...params }, params.rootType),
      );
    } catch (error) {
      const message = String(error?.message || '').toLowerCase();
      const missingViewRoute =
        Number(error?.status) === 404 &&
        (message.includes('/api/drive/view') || error?.code === 'API_ROUTE_NOT_FOUND');
      if (!missingViewRoute) {
        throw error;
      }

      // Backward-compatible fallback for deployments missing /api/drive/view.
      const rootType = params.rootType || options.rootType;
      const [listResult, pathResult] = await Promise.allSettled([
        driveList(firebase, folderId, { rootType }),
        drivePath(firebase, folderId, { rootType }),
      ]);

      const listData = listResult.status === 'fulfilled' ? listResult.value : null;
      const pathData = pathResult.status === 'fulfilled' ? pathResult.value : null;

      if (!listData && !pathData) {
        throw error;
      }

      return {
        folderId: listData?.folderId || pathData?.folderId || folderId || null,
        breadcrumb: pathData?.path || [],
        items: listData?.files || [],
        fallback: true,
      };
    }
  })();

export const driveCreateFolder = async (firebase, parentId, name, options = {}) =>
  apiFetch(firebase, '/api/drive/folder', {
    method: 'POST',
    body: JSON.stringify({ parentId, name }),
  }, withRoot(null, options.rootType));

export const driveUpload = async (firebase, parentId, file, options = {}) => {
  const formData = new FormData();
  formData.append('parentId', parentId);
  formData.append('file', file);
  if (options.appProperties) {
    formData.append('appProperties', JSON.stringify(options.appProperties));
  }

  return apiFetch(firebase, '/api/drive/upload', {
    method: 'POST',
    body: formData,
  }, withRoot(null, options.rootType));
};

export const driveUpdate = async (firebase, fileId, file, options = {}) => {
  const formData = new FormData();
  formData.append('fileId', fileId);
  formData.append('file', file);
  if (options.name) {
    formData.append('name', options.name);
  }
  if (options.appProperties) {
    formData.append('appProperties', JSON.stringify(options.appProperties));
  }

  return apiFetch(firebase, '/api/drive/update', {
    method: 'POST',
    body: formData,
  }, withRoot(null, options.rootType));
};

export const driveFind = async (firebase, appKey, appValue, options = {}) =>
  apiFetch(
    firebase,
    '/api/drive/find',
    { method: 'GET' },
    withRoot({ appKey, appValue }, options.rootType),
  );

export const driveRename = async (firebase, fileId, name, options = {}) =>
  apiFetch(firebase, '/api/drive/rename', {
    method: 'PATCH',
    body: JSON.stringify({ fileId, name }),
  }, withRoot(null, options.rootType));

export const driveMove = async (firebase, fileId, newParentId, options = {}) =>
  apiFetch(firebase, '/api/drive/move', {
    method: 'POST',
    body: JSON.stringify({ fileId, newParentId }),
  }, withRoot(null, options.rootType));

export const driveDelete = async (firebase, fileId, options = {}) =>
  apiFetch(
    firebase,
    '/api/drive/delete',
    { method: 'DELETE' },
    withRoot({ fileId }, options.rootType),
  );

export const isDriveFolder = (file) => file?.mimeType === 'application/vnd.google-apps.folder';
